/* TotoPets motion + interaction layer. Vanilla JS, no dependencies. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ *
   * 1. Reveal observer
   * ------------------------------------------------------------------ */
  function initReveals(root) {
    var nodes = (root || document).querySelectorAll('[data-reveal],[data-fade],[data-stagger],.tp-rule');
    if (!nodes.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    nodes.forEach(function (n) {
      if (n.classList.contains('is-in')) return;
      io.observe(n);
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Mask reveals (kept separate so images can start a touch earlier)
   * ------------------------------------------------------------------ */
  function initMasks(root) {
    var nodes = (root || document).querySelectorAll('[data-mask]');
    if (!nodes.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.04 });

    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ------------------------------------------------------------------ *
   * 3. Parallax — one rAF loop, only for elements currently on screen
   * ------------------------------------------------------------------ */
  var parallaxItems = [];
  var parallaxLive = [];
  var rafId = null;

  function collectParallax(root) {
    if (reduced) return;
    var nodes = (root || document).querySelectorAll('[data-parallax]');
    if (!nodes.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var item = parallaxItems.find(function (p) { return p.el === e.target; });
        if (!item) return;
        item.visible = e.isIntersecting;
      });
      parallaxLive = parallaxItems.filter(function (p) { return p.visible; });
      if (parallaxLive.length && rafId === null) tick();
    }, { rootMargin: '20% 0px 20% 0px' });

    nodes.forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-parallax')) || 0.16;
      parallaxItems.push({ el: el, strength: strength, visible: false });
      el.style.setProperty('--tp-ps', String(1 + strength * 1.1));
      io.observe(el);
    });
  }

  function tick() {
    rafId = null;
    if (!parallaxLive.length) return;
    var vh = window.innerHeight;

    for (var i = 0; i < parallaxLive.length; i++) {
      var p = parallaxLive[i];
      var r = p.el.getBoundingClientRect();
      // progress: -1 (just below viewport) .. 1 (just above)
      var progress = ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2);
      if (progress > 1) progress = 1;
      if (progress < -1) progress = -1;
      var shift = progress * r.height * p.strength * 0.5;
      p.el.style.setProperty('--tp-py', shift.toFixed(2) + 'px');
    }

    rafId = requestAnimationFrame(tick);
  }

  function wakeParallax() {
    if (rafId === null && parallaxLive.length) rafId = requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------------------ *
   * 4. Header — transparent over the hero, solid after scrolling
   * ------------------------------------------------------------------ */
  function initHeader() {
    var header = document.querySelector('[data-tp-header]');
    if (!header) return;

    var toggle = header.querySelector('[data-tp-menu-toggle]');
    var panel = document.querySelector('[data-tp-menu-panel]');

    var last = -1;
    function onScroll() {
      var solid = window.scrollY > 24 ? 1 : 0;
      if (solid !== last) {
        header.classList.toggle('is-solid', !!solid);
        last = solid;
      }
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    if (toggle && panel) {
      toggle.addEventListener('click', function () {
        var open = header.classList.toggle('is-open');
        panel.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.style.overflow = open ? 'hidden' : '';
      });
      panel.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', function () {
          header.classList.remove('is-open');
          panel.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          document.documentElement.style.overflow = '';
        });
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * 5. Hero load sequence
   * ------------------------------------------------------------------ */
  function initHero() {
    var hero = document.querySelector('[data-tp-hero]');
    if (!hero) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.documentElement.classList.remove('tp-boot');
        hero.classList.add('is-loaded');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 6. Horizontal scroll panels
   * ------------------------------------------------------------------ */
  function initHorizontal() {
    var sections = document.querySelectorAll('[data-tp-horizontal]');
    if (!sections.length) return;

    sections.forEach(function (section) {
      var track = section.querySelector('[data-tp-track]');
      var sticky = section.querySelector('[data-tp-sticky]');
      if (!track || !sticky) return;

      var desktop = window.matchMedia('(min-width: 900px)');
      var distance = 0;
      var running = false;

      function measure() {
        if (!desktop.matches || reduced) {
          section.style.height = '';
          track.style.transform = '';
          section.classList.remove('is-pinned');
          running = false;
          return;
        }
        section.classList.add('is-pinned');
        distance = Math.max(0, track.scrollWidth - window.innerWidth);
        section.style.height = (window.innerHeight + distance) + 'px';
        running = true;
        update();
      }

      function update() {
        if (!running) return;
        var r = section.getBoundingClientRect();
        var p = -r.top / (r.height - window.innerHeight || 1);
        if (p < 0) p = 0;
        if (p > 1) p = 1;
        track.style.transform = 'translate3d(' + (-p * distance).toFixed(2) + 'px,0,0)';
      }

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', measure);
      if (desktop.addEventListener) desktop.addEventListener('change', measure);
      measure();
      setTimeout(measure, 400);
    });
  }

  /* ------------------------------------------------------------------ *
   * 7. Before / after comparison
   * ------------------------------------------------------------------ */
  function initCompare() {
    document.querySelectorAll('[data-tp-compare]').forEach(function (root) {
      var handle = root.querySelector('[data-tp-compare-handle]');
      var after = root.querySelector('[data-tp-compare-after]');
      if (!handle || !after) return;
      var dragging = false;

      function set(pct) {
        pct = Math.max(2, Math.min(98, pct));
        root.style.setProperty('--tp-split', pct + '%');
      }

      function fromEvent(e) {
        var r = root.getBoundingClientRect();
        var x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
        set((x / r.width) * 100);
      }

      function start(e) { dragging = true; root.classList.add('is-dragging'); fromEvent(e); }
      function move(e) { if (dragging) { fromEvent(e); if (e.cancelable) e.preventDefault(); } }
      function end() { dragging = false; root.classList.remove('is-dragging'); }

      handle.addEventListener('mousedown', start);
      handle.addEventListener('touchstart', start, { passive: true });
      root.addEventListener('mousedown', start);
      window.addEventListener('mousemove', move);
      window.addEventListener('touchmove', move, { passive: false });
      window.addEventListener('mouseup', end);
      window.addEventListener('touchend', end);

      handle.addEventListener('keydown', function (e) {
        var cur = parseFloat(getComputedStyle(root).getPropertyValue('--tp-split')) || 50;
        if (e.key === 'ArrowLeft') { set(cur - 4); e.preventDefault(); }
        if (e.key === 'ArrowRight') { set(cur + 4); e.preventDefault(); }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 8. Accordion
   * ------------------------------------------------------------------ */
  function initAccordion() {
    document.querySelectorAll('[data-tp-accordion]').forEach(function (root) {
      root.querySelectorAll('[data-tp-acc-trigger]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-tp-acc-item]');
          var panel = item.querySelector('[data-tp-acc-panel]');
          var open = item.classList.contains('is-open');

          root.querySelectorAll('[data-tp-acc-item].is-open').forEach(function (other) {
            if (other === item) return;
            other.classList.remove('is-open');
            other.querySelector('[data-tp-acc-panel]').style.height = '0px';
            other.querySelector('[data-tp-acc-trigger]').setAttribute('aria-expanded', 'false');
          });

          if (open) {
            panel.style.height = '0px';
            item.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('is-open');
            panel.style.height = panel.scrollHeight + 'px';
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 9. Hotspots
   * ------------------------------------------------------------------ */
  function initHotspots() {
    document.querySelectorAll('[data-tp-hotspot]').forEach(function (dot) {
      dot.addEventListener('click', function () {
        var parent = dot.closest('[data-tp-hotspots]');
        if (!parent) return;
        var wasOpen = dot.classList.contains('is-open');
        parent.querySelectorAll('[data-tp-hotspot].is-open').forEach(function (d) { d.classList.remove('is-open'); });
        if (!wasOpen) dot.classList.add('is-open');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 10. Sticky add to cart
   * ------------------------------------------------------------------ */
  function initStickyBar() {
    var bar = document.querySelector('[data-tp-sticky-bar]');
    if (!bar) return;
    var anchor = document.querySelector('[data-tp-sticky-anchor]') || document.querySelector('[data-tp-hero]');
    if (!anchor) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ------------------------------------------------------------------ *
   * 11. Product form — variant switching, quantity, AJAX add
   * ------------------------------------------------------------------ */
  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    if (!format) return value;
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function initProductForms() {
    document.querySelectorAll('[data-tp-product]').forEach(function (root) {
      var dataEl = root.querySelector('[data-tp-variants]');
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = root.querySelector('[data-tp-variant-id]');
      var priceEl = root.querySelector('[data-tp-price]');
      var compareEl = root.querySelector('[data-tp-compare-price]');
      var saveEl = root.querySelector('[data-tp-save]');
      var submit = root.querySelector('[data-tp-submit]');
      var format = root.getAttribute('data-money-format') || '{{amount}} EUR';

      function select(variant) {
        if (!variant) return;
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = money(variant.price, format);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = money(variant.compare_at_price, format);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (saveEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
            saveEl.textContent = 'Save ' + pct + '%';
            saveEl.hidden = false;
          } else {
            saveEl.hidden = true;
          }
        }
        if (submit) {
          submit.disabled = !variant.available;
          var label = submit.querySelector('[data-tp-submit-label]');
          if (label) label.textContent = variant.available ? submit.getAttribute('data-label') : 'Sold out';
        }
      }

      root.querySelectorAll('[data-tp-option]').forEach(function (input) {
        input.addEventListener('change', function () {
          var chosen = [];
          root.querySelectorAll('[data-tp-option]:checked').forEach(function (c) { chosen.push(c.value); });
          var match = variants.find(function (v) {
            return v.options.every(function (o, i) { return o === chosen[i]; });
          });
          select(match);
        });
      });

      root.querySelectorAll('[data-tp-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var field = root.querySelector('[data-tp-qty]');
          if (!field) return;
          var next = parseInt(field.value, 10) + parseInt(btn.getAttribute('data-tp-qty-step'), 10);
          field.value = Math.max(1, next);
        });
      });

      var form = root.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (submit) submit.classList.add('is-loading');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(idInput.value),
              quantity: Number((root.querySelector('[data-tp-qty]') || {}).value || 1)
            }]
          })
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (submit) submit.classList.remove('is-loading');
            if (data && data.status && data.status !== 200) {
              window.location.href = '/cart';
              return;
            }
            showConfirm(data);
          })
          .catch(function () {
            if (submit) submit.classList.remove('is-loading');
            form.submit();
          });
      });
    });
  }

  function showConfirm(item) {
    var panel = document.querySelector('[data-tp-confirm]');
    if (!panel) { window.location.href = '/cart'; return; }
    var titleEl = panel.querySelector('[data-tp-confirm-title]');
    var metaEl = panel.querySelector('[data-tp-confirm-meta]');
    var imgEl = panel.querySelector('[data-tp-confirm-image]');

    if (item) {
      if (titleEl && item.product_title) titleEl.textContent = item.product_title;
      if (metaEl) {
        var bits = [];
        if (item.variant_title && item.variant_title !== 'Default Title') bits.push(item.variant_title);
        if (item.quantity) bits.push('Qty ' + item.quantity);
        metaEl.textContent = bits.join('  ·  ');
      }
      if (imgEl && item.image) { imgEl.src = item.image; imgEl.hidden = false; }
    }

    panel.classList.add('is-open');
    clearTimeout(showConfirm._t);
    showConfirm._t = setTimeout(function () { panel.classList.remove('is-open'); }, 6500);

    var close = panel.querySelector('[data-tp-confirm-close]');
    if (close && !close._bound) {
      close._bound = true;
      close.addEventListener('click', function () { panel.classList.remove('is-open'); });
    }
  }

  /* ------------------------------------------------------------------ *
   * 12. Gallery
   * ------------------------------------------------------------------ */
  function initGallery() {
    document.querySelectorAll('[data-tp-gallery]').forEach(function (root) {
      var main = root.querySelector('[data-tp-gallery-main]');
      root.querySelectorAll('[data-tp-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          if (!main) return;
          main.src = thumb.getAttribute('data-full');
          main.srcset = '';
          root.querySelectorAll('[data-tp-gallery-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */
  function boot() {
    initReveals();
    initMasks();
    collectParallax();
    wakeParallax();
    initHeader();
    initHero();
    initHorizontal();
    initCompare();
    initAccordion();
    initHotspots();
    initStickyBar();
    initProductForms();
    initGallery();
    window.addEventListener('scroll', wakeParallax, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-render support */
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    initMasks(e.target);
    boot();
  });
})();
